/**
 * COMPANY ROLE + THE ADAPTIVE CARD DEFAULT (two-part assignee, Feature A).
 *
 * A card has two assignee slots: Accountable (`assignee`) and Responsible
 * (`responsible`, the doer). When a person creates a card and self-assigns,
 * which slot that lands in follows WHO THEY ARE — a manager is Accountable, a
 * doer is Responsible — so neither manager-led nor doer-led teams have to touch
 * a thing per card.
 *
 * Three inputs decide it, most specific first:
 *   1. the person's own stance override (`Profile.cardDefaultStance`)
 *   2. the board's override (`ProjectMeta.cardDefaults`)
 *   3. the person's company role's default slot
 * …falling back to Accountable when nothing is set (which is exactly the old
 * single-assignee behaviour: the creator becomes the card's one owner).
 *
 * This module is pure — no store, no React — so the three create paths
 * (the card form, quick-add, add-from-plan) can all share one answer.
 */

/** The standing RACI role a person holds across the workspace. */
export type CompanyRole =
  | 'executive_director'
  | 'manager'
  | 'pm'
  | 'business_analyst'
  | 'team_member'
  | 'vendor';

/** How a self-assignment fills a slot — a person override or a board override. */
export type CardStance = 'follow' | 'accountable' | 'responsible' | 'none';

/** The whole word for a company role, as it reads in the workspace. */
export const COMPANY_ROLE_LABEL: Record<CompanyRole, string> = {
  executive_director: 'Executive Director',
  manager: 'Manager',
  pm: 'Project Manager',
  business_analyst: 'Business Analyst',
  team_member: 'Team member',
  vendor: 'External / Vendor',
};

/**
 * The order the roles read in a picker — accountable-leaning first, then the
 * doer-leaning ones, with `vendor` last. `vendor` was held back until the
 * external vendor tier (Feature B) shipped; it now ships, so the role is a live
 * option (B-08).
 */
export const COMPANY_ROLE_ORDER: CompanyRole[] = [
  'executive_director',
  'manager',
  'pm',
  'business_analyst',
  'team_member',
  'vendor',
];

/** The roles offered in the picker. With the vendor tier shipped (Feature B),
 *  External / Vendor is now a live choice, so this is the full order — choosing
 *  it defaults the person to Responsible AND turns on the Vendor name-tag. */
export const COMPANY_ROLE_PICK: CompanyRole[] = COMPANY_ROLE_ORDER;

/** Which slot a company role fills by default: doers are Responsible, everyone
 *  else (and the unset case) is Accountable. */
export function roleDefaultSlot(role?: CompanyRole | null): 'accountable' | 'responsible' {
  return role === 'business_analyst' || role === 'team_member' || role === 'vendor'
    ? 'responsible'
    : 'accountable';
}

/** The person-override stance labels (Workspace › People). */
export const STANCE_LABEL: Record<CardStance, string> = {
  follow: 'Follow company role',
  accountable: 'Always Accountable',
  responsible: 'Always Responsible',
  none: "Don't auto-assign",
};

/** The board-override labels (Settings › Workflow › Card defaults). */
export const BOARD_CARD_DEFAULT_LABEL: Record<CardStance, string> = {
  follow: "Follow each person's company role",
  accountable: 'Accountable',
  responsible: 'Responsible',
  none: "Don't auto-assign",
};

export const CARD_STANCE_ORDER: CardStance[] = ['follow', 'accountable', 'responsible', 'none'];

/**
 * Resolve which slot a creator's self-assignment fills: their explicit stance
 * wins; else the board's explicit override; else their company-role default.
 * `follow` (or unset) at a level means "defer to the next one down".
 */
export function resolveCreatorSlot(opts: {
  personStance?: CardStance | null;
  companyRole?: CompanyRole | null;
  boardDefault?: CardStance | null;
}): 'accountable' | 'responsible' | 'none' {
  const p = opts.personStance;
  if (p === 'accountable' || p === 'responsible' || p === 'none') return p;
  const b = opts.boardDefault;
  if (b === 'accountable' || b === 'responsible' || b === 'none') return b;
  return roleDefaultSlot(opts.companyRole);
}

/**
 * The two slots a new card is born with, from the creator's resolved stance.
 * `soleAssistant` is the assignee string of the one registered assistant when
 * exactly one exists (src/assistant.ts `defaultAssignee`): the old single default
 * now lands in the DOER slot (Feature A re-point), filling Responsible when the
 * creator's own stance has not already taken it.
 */
export function cardCreateDefault(opts: {
  me: string;
  personStance?: CardStance | null;
  companyRole?: CompanyRole | null;
  boardDefault?: CardStance | null;
  soleAssistant?: string;
}): { assignee: string; responsible: string } {
  const me = (opts.me ?? '').trim().toLowerCase();
  const slot = resolveCreatorSlot(opts);
  const assignee = slot === 'accountable' && me ? me : '';
  let responsible = slot === 'responsible' && me ? me : '';
  if (opts.soleAssistant && !responsible) responsible = opts.soleAssistant;
  return { assignee, responsible };
}
